/** Caps for in-browser exceljs unzip (bundled inside @js-preview/excel). */
const MAX_COMPRESSED_BYTES = 25 * 1024 * 1024;
const MAX_UNCOMPRESSED_BYTES = 80 * 1024 * 1024;
const MAX_ZIP_ENTRIES = 2_500;
const ZIP_EOCD_MAX_COMMENT = 65_535;
const ZIP_EOCD_SIG = 0x06054b50;
const ZIP_CD_SIG = 0x02014b50;
const ZIP64_SENTINEL = 0xffffffff;

const u16 = (view: DataView, offset: number) => view.getUint16(offset, true);
const u32 = (view: DataView, offset: number) => view.getUint32(offset, true);

const isZipMagic = (bytes: Uint8Array) => bytes.length >= 8 && bytes[0] === 0x50 && bytes[1] === 0x4b;
const isOleMagic = (bytes: Uint8Array) =>
  bytes.length >= 8 && bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0;

const findZipEocdOffset = (bytes: Uint8Array, view: DataView): number => {
  const min = Math.max(0, bytes.byteLength - 22 - ZIP_EOCD_MAX_COMMENT);
  for (let i = bytes.byteLength - 22; i >= min; i -= 1) {
    if (u32(view, i) === ZIP_EOCD_SIG) return i;
  }
  throw new Error("Spreadsheet archive is unreadable.");
};

const zipEntryNameIsUnsafe = (name: string) =>
  name.includes("..") || name.startsWith("/") || name.startsWith("\\") || name.includes("\\");

const assertSafeZipLimits = (bytes: Uint8Array) => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const eocd = findZipEocdOffset(bytes, view);
  const entryCount = u16(view, eocd + 10);
  const cdSize = u32(view, eocd + 12);
  const cdOffset = u32(view, eocd + 16);

  if (entryCount > MAX_ZIP_ENTRIES) {
    throw new Error("Spreadsheet has too many files to preview.");
  }
  if (cdOffset === ZIP64_SENTINEL || cdSize === ZIP64_SENTINEL || entryCount === 0xffff) {
    throw new Error("Spreadsheet archive is too large to preview.");
  }
  if (cdOffset + cdSize > bytes.byteLength) {
    throw new Error("Spreadsheet archive is unreadable.");
  }

  let cursor = cdOffset;
  let totalUncompressed = 0;
  const zipNameDecoder = new TextDecoder("latin1");
  for (let i = 0; i < entryCount; i += 1) {
    if (cursor + 46 > bytes.byteLength || u32(view, cursor) !== ZIP_CD_SIG) {
      throw new Error("Spreadsheet archive is unreadable.");
    }
    const compressed = u32(view, cursor + 20);
    const uncompressed = u32(view, cursor + 24);
    const nameLen = u16(view, cursor + 28);
    const extraLen = u16(view, cursor + 30);
    const commentLen = u16(view, cursor + 32);
    if (compressed === ZIP64_SENTINEL || uncompressed === ZIP64_SENTINEL) {
      throw new Error("Spreadsheet archive is too large to preview.");
    }
    totalUncompressed += uncompressed;
    if (totalUncompressed > MAX_UNCOMPRESSED_BYTES) {
      throw new Error("Spreadsheet is too large to preview.");
    }
    const nameStart = cursor + 46;
    const nameEnd = nameStart + nameLen;
    if (nameEnd > bytes.byteLength) {
      throw new Error("Spreadsheet archive is unreadable.");
    }
    const name = zipNameDecoder.decode(bytes.subarray(nameStart, nameEnd));
    if (zipEntryNameIsUnsafe(name)) {
      throw new Error("Spreadsheet archive is unreadable.");
    }
    cursor = nameEnd + extraLen + commentLen;
  }
};

export const assertSafeSpreadsheetPayload = (bytes: Uint8Array) => {
  if (bytes.byteLength > MAX_COMPRESSED_BYTES) {
    throw new Error("Spreadsheet is too large to preview.");
  }
  if (!isZipMagic(bytes) && !isOleMagic(bytes)) {
    throw new Error("Invalid file format. Expected Excel file.");
  }
  if (isZipMagic(bytes)) {
    assertSafeZipLimits(bytes);
  }
};

const isSafeHttpUrl = (href: string): boolean => {
  try {
    const url = new URL(href, window.location.href);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

/** Blocks javascript:/data:/file: navigation if the grid emits an <a>. */
export const preventUnsafePreviewNavigation = (event: Event) => {
  const node = event.target;
  if (!(node instanceof Element)) return;
  const anchor = node.closest("a");
  if (!anchor) return;
  const href = anchor.getAttribute("href");
  if (href == null || href === "" || href === "#") return;
  if (!isSafeHttpUrl(href)) {
    event.preventDefault();
    event.stopPropagation();
  }
};

/**
 * x-spreadsheet hit-tests with offsetX/offsetY. Those are relative to event.target,
 * and window mousemove during drag is relative to whatever is under the cursor.
 * Rewrite both to overlay-local CSS pixels (including ancestor scale).
 */
export const rewriteMouseOffsetRelativeTo = (overlay: HTMLElement, event: MouseEvent) => {
  const rect = overlay.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return;
  const x = ((event.clientX - rect.left) * overlay.offsetWidth) / rect.width;
  const y = ((event.clientY - rect.top) * overlay.offsetHeight) / rect.height;
  Object.defineProperty(event, "offsetX", { configurable: true, get: () => x });
  Object.defineProperty(event, "offsetY", { configurable: true, get: () => y });
};
